import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { Shift } from '../types/shifts';

export type { Shift };

interface ShiftsState {
  shifts: Shift[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ShiftsState = {
  shifts: [],
  isLoading: false,
  error: null,
};

const shiftsSlice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    setShifts(state, action: PayloadAction<Shift[]>) {
      state.shifts = action.payload;
    },
    upsertShift(state, action: PayloadAction<Shift>) {
      const idx = state.shifts.findIndex(
        (s) => s.shiftId === action.payload.shiftId && s.editPointDate === action.payload.editPointDate
      );
      if (idx !== -1) {
        state.shifts[idx] = action.payload;
      } else {
        state.shifts.push(action.payload);
      }
    },
    removeShiftsByShiftId(state, action: PayloadAction<string>) {
      state.shifts = state.shifts.filter((s) => s.shiftId !== action.payload);
    },
    removeShiftEditPoint(state, action: PayloadAction<{ shiftId: string; editPointDate: string }>) {
      state.shifts = state.shifts.filter(
        (s) => !(s.shiftId === action.payload.shiftId && s.editPointDate === action.payload.editPointDate)
      );
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const { setShifts, upsertShift, removeShiftsByShiftId, removeShiftEditPoint, setLoading, setError } =
  shiftsSlice.actions;

export const fetchShifts =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.listShifts();
      dispatch(setShifts(response.data ?? []));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load shifts'));
    } finally {
      dispatch(setLoading(false));
    }
  };

export default shiftsSlice.reducer;
